import {
    Box,
    Typography,
    Card,
    CardContent,
    CardHeader,
    TextField,
    Button,
    Grid,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    CircularProgress,
    Stack,
} from "@mui/material"
import { Container } from "../components/Container"
import Layout from "../components/Layout"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { StatusAPI } from "../api/StatusAPI"
import { HexColorPicker } from "react-colorful"
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material"

export const ConfigStatus = () => {
    const [statusList, setStatusList] = useState([])
    const [loading, setLoading] = useState(false)
    const [editingStatus, setEditingStatus] = useState(null)
    const [colorPickerOpen, setColorPickerOpen] = useState(false)
    const [newStatusModalOpen, setNewStatusModalOpen] = useState(false)

    const [newStatusList, setNewStatusList] = useState([])
    const [editedStatusList, setEditedStatusList] = useState([])
    
    const methods = useForm({
        defaultValues: {
            name: '',
            type: 'PROGRESS',
            hex_color: '#000000'
        }
    })
    
    const methodsEdit = useForm({
        defaultValues: {
            name: '',
            type: 'PROGRESS',
            hex_color: '#000000'
        }
    })
    

    const loadData = async () => {
        try {
            setLoading(true)
            const response = await StatusAPI.getConfigStatus();
            setStatusList(response.data.status || [])
        } catch (error) {
            console.error("Erro ao carregar status:", error)
        } finally {
            setLoading(false)
        }
    }

    const handleEditStatus = (status) => {
        setEditingStatus({ ...status })

        methodsEdit.reset({
            name: status.name,
            type: status.type,
            hex_color: status.hex_color
        })
        
        setColorPickerOpen(true)
    }

    const handleSaveStatus = () => {
        const formData = methodsEdit.getValues()

        const editedStatus = {
            ...editingStatus,
            name: formData.name,
            type: formData.type,
            hex_color: formData.hex_color
        }
        
        setEditedStatusList(prev => {
            const filtered = prev.filter(item => item.id !== editingStatus.id)
            return [...filtered, editedStatus]
        })

        setColorPickerOpen(false)
        setEditingStatus(null)
    }

    const handleCancelEdit = () => {
        setColorPickerOpen(false)
        setEditingStatus(null)
        methodsEdit.reset()
    }

    const handleCancelAllChanges = () => {
        setColorPickerOpen(false)
        setEditingStatus(null)
        setNewStatusModalOpen(false)
        setNewStatusList([])
        setEditedStatusList([])
        methods.reset()
        methodsEdit.reset()
 
        loadData()
 
    }

    const handleSaveAllChanges = async () => {
        try {
            setLoading(true)            
            const statusListToSend = getCombinedStatusList().map(status => {
                if (status.id.toString().startsWith('temp_')) {
                    const { id, created_at, ...statusWithoutTempFields } = status
                    return statusWithoutTempFields
                }
                return {
                    id: status.id,
                    name: status.name,
                    type: status.type,
                    hex_color: status.hex_color
                }
            })
            
            const response = await StatusAPI.saveAllStatus(statusListToSend)
            
            if (response.success) {
                setNewStatusList([])
                setEditedStatusList([])
                methods.reset()
                methodsEdit.reset()
                await loadData()
            }
        } catch (error) {
            console.error("Erro ao salvar alterações:", error)
        } finally {
            setLoading(false)
        }
    }

    const handleOpenModalNewStatus = () => {
        methods.reset()
        setNewStatusModalOpen(true)
    }

    const handleCloseModalNewStatus = () => {
        setNewStatusModalOpen(false)
        methods.reset()
    }

    const handleSaveNewStatus = (data) => {
        const newStatusWithId = {
            ...data,
            id: `temp_${Date.now()}`,
        }
        
        setNewStatusList(prev => [...prev, newStatusWithId])
        
        setNewStatusModalOpen(false)
        methods.reset()
    }

    const getStatusTypeLabel = (type) => {
        const types = {
            'PROGRESS': 'Em Andamento',
            'FINALIZED': 'Finalizado',
            'CANCELED': 'Cancelado',
            'EMPTY': 'Customizado'
        }
        return types[type] || type
    }

    const getStatusTypeColor = (type) => {
        const colors = {
            'PROGRESS': 'success',
            'FINALIZED': 'primary',
            'CANCELED': 'default'
        }
        return colors[type] || 'default'
    }

    const getCombinedStatusList = () => {
        const updatedOriginalStatus = statusList.map(status => {
            const editedStatus = editedStatusList.find(edited => edited.id === status.id)
            return editedStatus || status
        })
        
        return [...updatedOriginalStatus, ...newStatusList]
    }

    const hasPendingChanges = () => {
        return newStatusList.length > 0 || editedStatusList.length > 0
    }

    useEffect(() => {
        loadData();
    }, []);

    return (
        <Layout>
            <Box marginBottom='10px'>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Configurações de Status
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Status customizados
                </Typography>
            </Box>

            <Box marginBottom="10px" width="100%" display="flex" justifyContent="flex-end">
                <Button
                    style={{ textTransform: "none" }}
                    color="primary"
                    variant="contained"
                    onClick={() => handleOpenModalNewStatus()}
                    disabled={loading}
                >
                    <Typography>
                        Novo Status
                    </Typography>
                </Button>
            </Box>

            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                {loading ? (
                    <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
                        <CircularProgress size={20} />
                    </Box>
                ) : getCombinedStatusList().length === 0 ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px" sx={{ color: '#9CA3AF' }}>
                        <Typography variant="body1" sx={{ color: '#6B7280' }}>
                            Nenhum status cadastrado
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        {getCombinedStatusList()?.map((status) => (
                            <Card key={status.id} sx={{ borderRadius: 2, boxShadow: 3 }}>
                                <CardHeader
                                    title={
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {status.name}
                                        </Typography>
                                    }
                                    action={
                                        <IconButton
                                            onClick={() => handleEditStatus(status)}
                                            color="primary"
                                            size="small"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    }
                                />
                                <CardContent>
                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <Box
                                            width={40}
                                            height={40}
                                            bgcolor={status.hex_color}
                                            borderRadius={1}
                                            border="2px solid #e0e0e0"
                                        />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                Cor: {status.hex_color}
                                            </Typography>
                                            <Chip
                                                label={getStatusTypeLabel(status.type)}
                                                color={getStatusTypeColor(status.type)}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                    </Box>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        {status.id.toString().startsWith('temp_') && (
                                            <Chip 
                                                label="Novo" 
                                                color="success" 
                                                size="small" 
                                                variant="outlined"
                                            />
                                        )}
                                        {editedStatusList.find(edited => edited.id === status.id) && !status.id.toString().startsWith('temp_') && (
                                            <Chip 
                                                label="Editado" 
                                                color="warning" 
                                                size="small" 
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}
            </Box>

            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Container sx={{ position: 'relative' }}>
                    <Box display="flex" justifyContent="space-between" flexDirection="column" height="100%">
                        <Grid container spacing={3}>
                    {getCombinedStatusList()?.map((status) => (
                        <Grid item xs={12} md={6} lg={4} key={status.id}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="h6" component="h2">
                                            {status.name}
                                        </Typography>
                                            {status.id.toString().startsWith('temp_') && (
                                                <Chip 
                                                    label="Novo" 
                                                    color="success" 
                                                    size="small" 
                                                    variant="outlined"
                                                />
                                            )}
                                            {editedStatusList.find(edited => edited.id === status.id) && !status.id.toString().startsWith('temp_') && (
                                                <Chip 
                                                    label="Editado" 
                                                    color="warning" 
                                                    size="small" 
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                        <IconButton
                                            onClick={() => handleEditStatus(status)}
                                            color="primary"
                                            size="small"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <Box
                                            width={40}
                                            height={40}
                                            bgcolor={status.hex_color}
                                            borderRadius={1}
                                            border="2px solid #e0e0e0"
                                        />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Cor: {status.hex_color}
                                            </Typography>
                                            <Chip
                                                label={getStatusTypeLabel(status.type)}
                                                color={getStatusTypeColor(status.type)}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                        </Grid>

                        <Box
                            width="100%"
                            display="flex"
                            justifyContent="flex-end"
                            className="gap-2"
                        >
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={handleCancelAllChanges}
                                color="inherit"
                                disabled={!hasPendingChanges()}
                                sx={{
                                    backgroundColor: 'background.paper',
                                    '&:hover': {
                                        backgroundColor: 'action.hover'
                                    }
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveAllChanges}
                                disabled={loading || !hasPendingChanges()}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark'
                                    }
                                }}
                            >
                                
                                {loading ? <CircularProgress/> : 'Salvar'}
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Dialog
                open={colorPickerOpen}
                onClose={handleCancelEdit}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Editar Status: {editingStatus?.name}
                </DialogTitle>
                <DialogContent>
                    <form onSubmit={methodsEdit.handleSubmit(handleSaveStatus)}>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Nome do Status"
                            margin="normal"
                                {...methodsEdit.register("name", { required: "Nome do status é obrigatório" })}
                                error={!!methodsEdit.formState.errors.name}
                                helperText={methodsEdit.formState.errors.name?.message}
                            />


                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Cor do Status
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                                <Box
                                    width={60}
                                    height={60}
                                    bgcolor={methodsEdit.watch("hex_color")}
                                    borderRadius={1}
                                    border="2px solid #e0e0e0"
                                />
                                <TextField
                                    label="Código da Cor"
                                    size="small"
                                    sx={{ width: 120 }}
                                        {...methodsEdit.register("hex_color")}
                                />
                            </Box>
                            <HexColorPicker
                                    color={methodsEdit.watch("hex_color")}
                                    onChange={(color) => methodsEdit.setValue("hex_color", color)}
                                style={{ width: '100%' }}
                            />
                        </Box>
                    </Box>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCancelEdit}
                        startIcon={<CancelIcon />}
                        color="inherit"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={methodsEdit.handleSubmit(handleSaveStatus)}
                        startIcon={<SaveIcon />}
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={newStatusModalOpen}
                onClose={handleCloseModalNewStatus}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Novo Status
                </DialogTitle>
                <DialogContent>
                    <form onSubmit={methods.handleSubmit(handleSaveNewStatus)}>
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Nome do Status"
                                margin="normal"
                                {...methods.register("name", { required: "Nome do status é obrigatório" })}
                                error={!!methods.formState.errors.name}
                                helperText={methods.formState.errors.name?.message}
                            />

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Cor do Status
                                </Typography>
                                <Box display="flex" alignItems="center" gap={2} mb={2}>
                                    <Box
                                        width={60}
                                        height={60}
                                        bgcolor={methods.watch("hex_color")}
                                        borderRadius={1}
                                        border="2px solid #e0e0e0"
                                    />
                                    <TextField
                                        label="Código da Cor"
                                        size="small"
                                        sx={{ width: 120 }}
                                        {...methods.register("hex_color")}
                                    />
                                </Box>
                                <HexColorPicker
                                    color={methods.watch("hex_color")}
                                    onChange={(color) => methods.setValue("hex_color", color)}
                                    style={{ width: '100%' }}
                                />
                            </Box>
                        </Box>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseModalNewStatus}
                        startIcon={<CancelIcon />}
                        color="inherit"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={methods.handleSubmit(handleSaveNewStatus)}
                        startIcon={<SaveIcon />}
                        variant="contained"
                        disabled={loading}
                    >
                        Adicionar
                    </Button>
                </DialogActions>
            </Dialog>

        </Layout >
    )
}